import React from 'react';
import { connect } from 'react-redux';
import { Button, Link, CancelButton } from '../ui';
import { EmailFormState, EmailTemplate, Placeholder } from '../../store/email/types';
import { AppState } from '../../store/state';
import { AppDispatch } from '../../store/store';
import { GridColumnProps } from '@progress/kendo-react-grid';
import ActionsCell from '../ActionsCell/ActionsCell';
import Loading from '../Loading/Loading';
import EmailDialog from '../dialogs/EmailDialog/EmailDialog';
import InvitationEmailForm from '../InvitationEmailForm/InvitationEmailForm';
import { BasicInvitationFormFields, InvitationDTO, PreviewRow } from '../../store/recruitment/types';
import recruitmentSlice from '../../store/recruitment/slice';
import emailSlice from '../../store/email/slice';
import { getInvitationEmailRender } from '../../store/recruitment/sagas';
import { ValidatedField } from '../../store/api.d';
import { INVITATION_INPUT_TYPE } from '../InvitationRecipientPicker/InvitationRecipientPicker';
import styled, { ThemeProvider } from 'styled-components';
import useTheme from '@material-ui/core/styles/useTheme';
import MyGridCore from '../MyGridCore/MyGridCore';
import { DEFAULT_FONT_SIZE } from '../MyGridRedux/MyGridRedux';
import { useTranslation } from 'react-i18next';
import EmailSending from '../EmailSending/EmailSending';

interface Props {
  input?: BasicInvitationFormFields[] | number[];
  type: INVITATION_INPUT_TYPE;
  formState: EmailFormState;
  preview?: PreviewRow[];
  email: InvitationDTO;
  totalToSend: number;
  placeholders: Placeholder[];
  templatesById: { [key: number]: EmailTemplate };
  removePreviewAction: () => void;
  createInvitationsAction: (data: InvitationDTO) => void;
  validationErrors?: ValidatedField[];
  onClose: () => void;
  onBack?: () => void;
}

const SendInvitationEmailForm: React.FC<Props> = ({
  input,
  type,
  formState,
  preview,
  email,
  totalToSend,
  removePreviewAction,
  createInvitationsAction,
  validationErrors,
  placeholders,
  onClose,
  onBack,
  templatesById,
}) => {
  const { t, i18n } = useTranslation();
  const [dialog, setDialog] = React.useState<JSX.Element | undefined>(undefined);
  const theme = useTheme();

  const MAX_PREVIEW_NUM = 20;

  switch (formState) {
    case 'COMPOSE':
      return (
        <InvitationEmailForm
          value={email}
          placeholders={placeholders}
          onSendHandler={(data) => {
            let args;
            switch (type) {
              case 'many':
              case 'single':
                args = { recipients: (input as BasicInvitationFormFields[]).slice(0, MAX_PREVIEW_NUM) };
                break;
              case 'ids':
                args = { ids: (input as number[]).slice(0, MAX_PREVIEW_NUM) };
                break;
            }
            createInvitationsAction({ mode: type, dry_run: true, ...data, ...args });
          }}
          validationErrors={validationErrors}
          onClose={onClose}
          back={onBack}
          isResend={type === 'ids'}
          templatesById={templatesById}
        />
      );
    case 'PREVIEW':
      const gridColumnProps: GridColumnProps[] = [];
      gridColumnProps.push(
        {
          field: 'recipient_name',
          title: t('Recipient name'),
        },
        {
          field: 'recipient_address',
          title: t('Recipient address'),
        },
        {
          field: 'subject',
          title: t('Subject'),
        },
        {
          field: 'body',
          title: t('Body'),
          cell: ActionsCell,
          filterable: false,
        },
      );

      const inputData: any[] = preview
        ? preview.map((previewRow) => ({
            ...previewRow,
            body: (
              <Link
                className="flex items-center cursor-pointer font-semibold"
                onClick={async () => {
                  if (email) {
                    const response = await getInvitationEmailRender({
                      email: previewRow.recipient_address,
                      target_role_id: previewRow.role_id,
                      first_name: previewRow.first_name,
                      last_name: previewRow.last_name,
                      body: email.body,
                      expiration_date: email.expiration_date,
                      concat: email.concat,
                    });
                    if (response.type === 'ok') {
                      setDialog(
                        <EmailDialog
                          show={true}
                          onClose={() => setDialog(undefined)}
                          html={response.value.data.html}
                        />,
                      );
                    }
                  }
                }}
              >
                {t('Preview Invitation')}
              </Link>
            ),
          }))
        : [];

      return (
        <ThemeProvider theme={theme}>
          <div className="mt-10">
            <div className="mb-2">
              {inputData.length >= 20 && <span>Preview of the first {inputData.length} invitations to be sent.</span>}
            </div>
            <MyGridCore
              allGridData={inputData}
              gridSettings={{
                gridColumnProps: gridColumnProps,
                advancedFilter: false,
                DATA_ITEM_KEY: '',
                SELECTED_FIELD: '',
                LABELABLE_ITEM_KEY: '',
                fontSize: DEFAULT_FONT_SIZE,
                dataState: {},
              }}
              selectedState={{}}
              dateFormat={null}
              selectable={false}
              showActionsColumn={false}
              showFilters={true}
            />
            <StyledButtons>
              <CancelButton onClick={onClose}>{t('Cancel')}</CancelButton>

              <Button type="button" variant="text" onClick={() => removePreviewAction()} className="font-semibold">
                {t('Previous')}
              </Button>
              {preview && preview.length > 0 && (
                <Button
                  type="button"
                  onClick={() => {
                    let args;
                    switch (type) {
                      case 'many':
                      case 'single':
                        args = { recipients: input as BasicInvitationFormFields[] };
                        break;
                      case 'ids':
                        args = { ids: input as number[] };
                        break;
                    }
                    createInvitationsAction({
                      mode: type,
                      dry_run: false,
                      subject: email.subject,
                      body: email.body,
                      expiration_date: email.expiration_date,
                      concat: email.concat,
                      ...args,
                    });
                  }}
                >
                  {t('Send')}
                </Button>
              )}
            </StyledButtons>
            {dialog}
          </div>
        </ThemeProvider>
      );
    case 'SENDING':
      return (
        <>
          <div className="mt-10">
            <EmailSending token={email.token ?? ''} />
          </div>

          <StyledButtons>
            <Button onClick={onClose}>{t('Close')}</Button>
          </StyledButtons>
        </>
      );
    case 'WAIT':
      return (
        <div className="m-5">
          <Loading compact={true} />
        </div>
      );
  }
};

const mapStateToProps = (state: AppState) => ({});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  removePreviewAction: () => dispatch(emailSlice.actions['REMOVE:PREVIEW']()),
  createInvitationsAction: (data: InvitationDTO) => dispatch(recruitmentSlice.actions.CREATE_INVITATIONS(data)),
});

const StyledButtons = styled.div`
  text-align: center;
  justify-content: center;
  margin-top: 2rem;
  border-top: 2px dotted #e3e5e6;
  padding: 1rem 0 0 0;
  position: sticky;
  bottom: 0;
  background-color: #fff;
  z-index: 10;
  padding-bottom: 1.5rem;
`;

export default connect(mapStateToProps, mapDispatchToProps)(SendInvitationEmailForm);
